'use client'

import { InfoCircle16, X24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Image,
  InfoBanner,
  Label,
  Navigation,
  Note,
  SelectionGroup,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import {
  getReturnShippingOptions,
  createCustomReturnShipment,
  ComplaintModel,
  transformGetReturnShippingOptionsResp,
  PricingSummaryModel,
  ReturnShippingOptionModel,
  ReturnShippingOption,
  ReturnShippingOptionPricing,
  clickEvent,
  viewEvent,
  getEscrowRefundEstimate,
  transformEscrowRefundEstimateResp,
  RefundElements,
  EscrowRefundEstimateModel,
  formatCurrencyAmount,
  CurrencyAmountModel,
} from '@marketplace-web/complaints/complaints-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import { isComplaintOrderSellerBusinessUser } from '../../pages/ComplaintCreation/utils'
import ComplaintCurrencyConversionModal from '../ComplaintCurrencyConversionModal'
import EstimatedRefundInformation from '../EstimatedRefundInformation'
import GenericErrorModal from '../GenericErrorModal'
import ComplaintOrderItems from '../RequestReturnModal/ComplaintOrderItems'
import ShipmentItems from '../RequestReturnModal/ShipmentItems'
import ReturnCurrencyConversionModal from './ReturnCurrencyConversionModal'
import { findRefundEstimateElement, findShippingOption, ModalKeys } from './utils'
import ShippingDiscountModal from '../ShippingDiscountModal'
import BuyerRefundInformation from '../BuyerRefundInformation'
import BuyerCurrencyConversionModal from '../BuyerCurrencyConversionModal'

type Props = {
  isOpen: boolean
  complaint?: ComplaintModel | null
  isComplaintLoading: boolean
  onClose: () => void
  onContinue: () => void
}

const ReturnShippingOptionsModal = ({
  isOpen,
  complaint,
  isComplaintLoading,
  onClose,
  onContinue,
}: Props) => {
  const { locale } = useIntl()
  const { track } = useTracking()
  const translate = useTranslate('conversation.actions.return_shipping_options.modal')
  const currencyConversionTranslation = useTranslate('checkout.order_summary')
  const asset = useAsset('/assets/shipping')
  const isNewRefundSummaryEnabled = useFeatureSwitch('web_new_refund_summary')

  const { conversation } = useConversationContext()
  const transactionId = conversation?.transaction?.id
  const isBundle = conversation?.transaction?.isBundle
  const complaintOrderItemsIds =
    complaint?.complaintOrderItems?.map(item => item.orderItemId).toString() || null
  const { showAllItemsReturnBanner } = complaint || {}

  const CARRIER_ICON_PLACEHOLDER = 'carrier-placeholder.svg'

  const [selectedShippingOption, setSelectedShippingOption] = useState<
    ReturnShippingOption | undefined
  >()
  const [activeModal, setActiveModal] = useState<ModalKeys | null>()
  const [initialRefundEstimate, setInitialRefundEstimate] =
    useState<EscrowRefundEstimateModel | null>()

  const {
    fetch: fetchRefundEstimate,
    transformedData: refundEstimate,
    isLoading: isRefundEstimateLoading,
  } = useFetch(getEscrowRefundEstimate, transformEscrowRefundEstimateResp)

  const estimatedRefundWithoutShipment = useMemo(() => {
    if (!initialRefundEstimate) return undefined

    const totalAmount = Number(initialRefundEstimate.totalEstimatedRefund.amount)
    const returnShippingElement = findRefundEstimateElement(
      initialRefundEstimate.refundElements,
      RefundElements.ReturnShipping,
    )

    const shippingAmount =
      returnShippingElement?.money && Math.abs(Number(returnShippingElement.money.amount))

    if (shippingAmount === undefined) return undefined

    return {
      amount: (totalAmount - shippingAmount).toString(),
      currencyCode: initialRefundEstimate.totalEstimatedRefund.currencyCode,
    }
  }, [initialRefundEstimate])

  const {
    fetch: fetchReturnShippingOptions,
    transformedData: returnShippingOptionsData,
    isLoading: isReturnShippingOptionsLoading,
  } = useFetch(getReturnShippingOptions, transformGetReturnShippingOptionsResp)

  const fetchData = useCallback(async () => {
    if (!isOpen || !transactionId) return
    setActiveModal(ModalKeys.ReturnShippingOptionsModal)

    const { error } = await fetchReturnShippingOptions(transactionId)

    if (error) setActiveModal(ModalKeys.GenericErrorModal)
  }, [isOpen, transactionId, fetchReturnShippingOptions, setActiveModal])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (!returnShippingOptionsData) return

    const integratedReturnShippingOption = findShippingOption(
      returnShippingOptionsData.returnShippingOptions,
      ReturnShippingOption.Integrated,
    )
    const customReturnShippingOption = findShippingOption(
      returnShippingOptionsData.returnShippingOptions,
      ReturnShippingOption.Custom,
    )

    if (integratedReturnShippingOption) {
      setSelectedShippingOption(ReturnShippingOption.Integrated)
    } else if (customReturnShippingOption) {
      setSelectedShippingOption(ReturnShippingOption.Custom)
    }
  }, [returnShippingOptionsData])

  const isUnifiedComplaintFlow = complaint?.complaintOrderItems?.length
  const integratedReturnShippingOption = findShippingOption(
    returnShippingOptionsData?.returnShippingOptions,
    ReturnShippingOption.Integrated,
  ) as ReturnShippingOptionModel
  const customReturnShippingOption = findShippingOption(
    returnShippingOptionsData?.returnShippingOptions,
    ReturnShippingOption.Custom,
  ) as ReturnShippingOptionModel
  const isIntegratedReturnShippingOptionSelected =
    selectedShippingOption === ReturnShippingOption.Integrated
  const isIntegratedAndNotPaidBySeller =
    integratedReturnShippingOption && !integratedReturnShippingOption?.sellerPaidForReturn

  useEffect(() => {
    if (!isOpen || !transactionId) return
    if (!isNewRefundSummaryEnabled) return
    if (!complaintOrderItemsIds) return
    if (!returnShippingOptionsData) return

    fetchRefundEstimate({
      orderId: transactionId,
      orderItemIds: complaintOrderItemsIds,
      integratedShipping: isIntegratedAndNotPaidBySeller,
    }).then(({ transformedData }) => transformedData && setInitialRefundEstimate(transformedData))
  }, [
    complaintOrderItemsIds,
    fetchRefundEstimate,
    isIntegratedAndNotPaidBySeller,
    isNewRefundSummaryEnabled,
    isOpen,
    returnShippingOptionsData,
    transactionId,
  ])

  useEffect(() => {
    if (!isOpen) return

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
    })

    track(
      viewEvent({
        screen: 'return_order',
        target: 'return_order',
        targetDetails,
      }),
    )
  }, [isOpen, track, transactionId])

  const { fetch: fetchCreateReturnShipment, isLoading: isCreateReturnShipmentLoading } = useFetch(
    createCustomReturnShipment,
  )

  const isMultipleShippingOptions = () => {
    if (!returnShippingOptionsData?.returnShippingOptions) return false

    return returnShippingOptionsData.returnShippingOptions.length > 1
  }

  const selectReturnShippingMethod = (returnShippingOption: ReturnShippingOption) => () => {
    setSelectedShippingOption(returnShippingOption)
  }

  const showCurrencyConversion = () => {
    setActiveModal(ModalKeys.ReturnCurrencyConversionModal)
  }

  const handleModalClose = () => {
    setSelectedShippingOption(undefined)
    setActiveModal(null)
    onClose()
  }

  const renderNavigation = () => {
    return (
      <>
        <Navigation
          right={
            <Button
              iconName={X24}
              theme="amplified"
              styling={Button.Styling.Flat}
              testId="return-shipping-options-close-button"
              onClick={handleModalClose}
            />
          }
          body={
            <Text
              as="h2"
              type={Text.Type.Title}
              width={Text.Width.Parent}
              alignment={Text.Alignment.Center}
              text={translate('title')}
            />
          }
        />
        <Divider />
      </>
    )
  }

  const renderShowAllItemsReturnInfoBanner = () => {
    if (!showAllItemsReturnBanner) return null

    return <InfoBanner body={<Text as="span" text={translate('all_items_return_banner')} />} />
  }

  const renderMultipleShippingOptionsTitle = () => (
    <Cell
      title={<Text as="h1" type={Text.Type.Heading} text={translate('shipping_method_title')} />}
    />
  )

  const renderOneShippingOptionTitle = () => (
    <Label type={Label.Type.Stacked} text={translate('one_shipping_method_title')} />
  )

  const renderTitle = () =>
    isMultipleShippingOptions()
      ? renderMultipleShippingOptionsTitle()
      : renderOneShippingOptionTitle()

  const renderShippingItems = () => {
    if (isComplaintLoading) return <ContentLoader styling={ContentLoader.Styling.Wide} />

    if (complaint?.complaintOrderItems?.length)
      return <ComplaintOrderItems complaintOrderItems={complaint.complaintOrderItems} />

    return <ShipmentItems transactionId={transactionId} />
  }

  const renderItemVerificationInfoBanner = () => {
    if (!integratedReturnShippingOption?.withAuthenticityCheck) return null

    return <InfoBanner body={<Text as="span" text={translate('offline_verification')} />} />
  }

  const renderElectronicsVerificationInfoBanner = () => {
    if (!integratedReturnShippingOption?.withElectronicsVerification) return null

    return (
      <InfoBanner body={<Text as="span" text={translate('electronics_verification')} html />} />
    )
  }

  const renderItemsNote = () => {
    if (!isBundle) return null

    if (isUnifiedComplaintFlow || isComplaintLoading) return null

    return <Note text={translate('bundle_items_note')} />
  }

  const handleShippingDiscountInfoClick = () => {
    track(
      clickEvent({
        screen: 'return_order',
        target: 'shipping_discount_details',
        targetDetails: JSON.stringify({
          transaction_id: transactionId,
        }),
      }),
    )

    setActiveModal(ModalKeys.ShippingDiscountModal)
  }

  const renderShippingPrice = (pricingSummary: PricingSummaryModel) => {
    if (pricingSummary.pricing !== ReturnShippingOptionPricing.Postage || !pricingSummary.price)
      return null

    const { price, priceBeforeDiscount } = pricingSummary

    if (!priceBeforeDiscount) {
      return (
        <Text
          as="h2"
          key={pricingSummary.pricing}
          type={Text.Type.Title}
          text={formatCurrencyAmount(pricingSummary.price, locale)}
        />
      )
    }

    return (
      <div className="u-flexbox u-align-items-center" key={price.amount}>
        <Text
          as="span"
          text={`${formatCurrencyAmount(price, locale)}`}
          type={Text.Type.Body}
          bold
          testId="return-shipping-options-modal-price-after-discount"
        />
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Text
          as="h3"
          text={formatCurrencyAmount(priceBeforeDiscount, locale)}
          type={Text.Type.Subtitle}
          strikethrough
          testId="return-shipping-options-modal-price-before-discount"
        />
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <button
          type="button"
          className="u-flexbox u-align-items-center u-cursor-pointer"
          onClick={handleShippingDiscountInfoClick}
          aria-label={translate('a11y.shipping_discount_info_icon')}
        >
          <Icon
            name={InfoCircle16}
            color={Icon.Color.GreyscaleLevel2}
            testId="return-shipping-options-modal-discount-info-icon"
          />
        </button>
      </div>
    )
  }

  const renderCarrierDiscountInfo = () => {
    if (!integratedReturnShippingOption) return null

    const postagePrice = integratedReturnShippingOption.pricingSummary?.find(
      pricingSummary => pricingSummary.pricing === ReturnShippingOptionPricing.Postage,
    )

    if (!postagePrice?.discount) return null

    return (
      <>
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Text
          as="span"
          text={`(-${postagePrice.discount.percent}%)`}
          theme="warning"
          testId="return-shipping-options-modal-discount-percent"
        />
      </>
    )
  }

  const renderIntegratedReturnShippingOption = () => {
    const { iconUrl, title, subtitle, pricingSummary, code, note } = integratedReturnShippingOption

    return (
      <SelectionGroup.SelectionItem
        key={code}
        showSelectionIndicator={isMultipleShippingOptions()}
        title={
          <div className="u-flexbox u-flex-direction-column">
            <div>
              <Image
                src={iconUrl}
                size={Image.Size.Regular}
                styling={Image.Styling.Rounded}
                fallbackSrc={asset(CARRIER_ICON_PLACEHOLDER)}
              />
              <Spacer orientation={Spacer.Orientation.Vertical} />
              <Text as="h2" type={Text.Type.Title} text={title} />
              {renderCarrierDiscountInfo()}
            </div>
            <Spacer />
            {note ? (
              <Text as="h2" type={Text.Type.Title} text={note} html />
            ) : (
              pricingSummary?.map(renderShippingPrice)
            )}
            <Spacer size={Spacer.Size.Large} />
          </div>
        }
        body={<Text as="p" text={subtitle} />}
        onClick={selectReturnShippingMethod(code)}
        isSelected={code === selectedShippingOption}
        testId="return-shipping-option-integrated"
      />
    )
  }

  const renderCustomReturnShippingOption = () => {
    const { iconUrl, title, subtitle, code } = customReturnShippingOption

    return (
      <SelectionGroup.SelectionItem
        key={code}
        showSelectionIndicator={isMultipleShippingOptions()}
        title={
          <>
            <Image src={iconUrl} size={Image.Size.Regular} styling={Image.Styling.Rounded} />
            <Spacer orientation={Spacer.Orientation.Vertical} />
            <Text type={Text.Type.Title} text={title} as="h2" />
            <Spacer size={Spacer.Size.Large} />
          </>
        }
        body={<Text text={subtitle} as="p" />}
        onClick={selectReturnShippingMethod(code)}
        testId="return-shipping-option-custom"
        isSelected={code === selectedShippingOption}
      />
    )
  }

  const renderReturnShippingOptions = () => {
    if (!returnShippingOptionsData?.returnShippingOptions?.length) return null

    return (
      <SelectionGroup layout={SelectionGroup.Layout.Default}>
        {returnShippingOptionsData.returnShippingOptions.map(option => {
          if (option.code === ReturnShippingOption.Integrated) {
            return renderIntegratedReturnShippingOption()
          }

          return renderCustomReturnShippingOption()
        })}
      </SelectionGroup>
    )
  }

  const getIntegratedShippingPrice = (
    pricing: ReturnShippingOptionPricing,
    fullOrderReturnPrice: CurrencyAmountModel,
  ): CurrencyAmountModel => {
    if (!isUnifiedComplaintFlow || !complaint?.estimatedRefund) return fullOrderReturnPrice

    if (
      pricing === ReturnShippingOptionPricing.Postage &&
      complaint.estimatedRefund.details.returnShipment
    )
      return complaint.estimatedRefund.details.returnShipment

    if (pricing === ReturnShippingOptionPricing.RefundSubtotal)
      return complaint.estimatedRefund.totalEstimatedRefund

    if (
      pricing === ReturnShippingOptionPricing.RefundTotal &&
      !integratedReturnShippingOption?.sellerPaidForReturn &&
      complaint.estimatedRefund.estimatedRefundWithoutShipment
    )
      return complaint.estimatedRefund.estimatedRefundWithoutShipment

    return complaint.estimatedRefund.totalEstimatedRefund
  }

  const getIntegratedShippingPriceWithNewEstimatedRefund = (
    pricing: ReturnShippingOptionPricing,
    fullOrderReturnPrice: CurrencyAmountModel,
  ): CurrencyAmountModel => {
    if (!isUnifiedComplaintFlow || !initialRefundEstimate) return fullOrderReturnPrice

    const returnShipment = findRefundEstimateElement(
      initialRefundEstimate.refundElements,
      RefundElements.ReturnShipping,
    )
    if (pricing === ReturnShippingOptionPricing.Postage && returnShipment?.money)
      return returnShipment.money

    if (pricing === ReturnShippingOptionPricing.RefundSubtotal)
      return initialRefundEstimate.totalEstimatedRefund

    if (
      pricing === ReturnShippingOptionPricing.RefundTotal &&
      !integratedReturnShippingOption?.sellerPaidForReturn &&
      estimatedRefundWithoutShipment
    )
      return estimatedRefundWithoutShipment

    return initialRefundEstimate.totalEstimatedRefund
  }

  const openRefund = () => {
    if (isNewRefundSummaryEnabled && transactionId && complaintOrderItemsIds) {
      fetchRefundEstimate({
        orderId: transactionId,
        orderItemIds: complaintOrderItemsIds,
        integratedShipping:
          isIntegratedReturnShippingOptionSelected &&
          !integratedReturnShippingOption?.sellerPaidForReturn,
      })
    }

    setActiveModal(ModalKeys.EstimatedRefundInformation)
  }

  const renderRefundInfoIcon = () => {
    if (!isUnifiedComplaintFlow) return null

    return (
      <>
        <Spacer orientation={Spacer.Orientation.Vertical} />
        <button
          className="u-flexbox u-align-items-center"
          onClick={openRefund}
          type="button"
          title={translate('a11y.refund_info_icon')}
          aria-label={translate('a11y.refund_info_icon')}
        >
          <Icon name={InfoCircle16} color={Icon.Color.GreyscaleLevel2} />
        </button>
      </>
    )
  }

  const getIntegratedShippingFormattedPrice = (pricing: PricingSummaryModel) => {
    if (isNewRefundSummaryEnabled) {
      return formatCurrencyAmount(
        getIntegratedShippingPriceWithNewEstimatedRefund(
          pricing.pricing,
          pricing.price as CurrencyAmountModel,
        ),
        locale,
      )
    }

    const isFormattedAsNegative = pricing.pricing === ReturnShippingOptionPricing.Postage

    return formatCurrencyAmount(
      getIntegratedShippingPrice(pricing.pricing, pricing.price as CurrencyAmountModel),
      locale,
      {
        formatAsNegative: isFormattedAsNegative,
      },
    )
  }

  const renderIntegratedShippingPriceOption = () => {
    return integratedReturnShippingOption?.pricingSummary?.map(pricing => {
      if (!pricing.price) return null

      const isLastElement = pricing.pricing === ReturnShippingOptionPricing.RefundTotal
      const isRefundSubtotal = pricing.pricing === ReturnShippingOptionPricing.RefundSubtotal

      return (
        <Fragment key={pricing.pricing}>
          {isLastElement && <Spacer size={Spacer.Size.Large} />}
          <Cell
            key={pricing.pricing}
            testId="return-shipping-option-integrated-price"
            styling={Cell.Styling.Tight}
            title={
              <div className="u-flexbox">
                <Text
                  as={isLastElement ? 'h2' : 'span'}
                  type={isLastElement ? Text.Type.Title : undefined}
                  text={pricing.title}
                />
                {isRefundSubtotal && renderRefundInfoIcon()}
              </div>
            }
            suffix={
              <Text
                as={isLastElement ? 'h2' : 'span'}
                type={isLastElement ? Text.Type.Title : undefined}
                text={
                  pricing.priceNote ? (
                    <Text as="span" text={pricing.priceNote} />
                  ) : (
                    getIntegratedShippingFormattedPrice(pricing)
                  )
                }
              />
            }
          />
        </Fragment>
      )
    })
  }

  const getCustomShippingPrice = (
    pricing: ReturnShippingOptionPricing,
    fullOrderReturnPrice: CurrencyAmountModel,
  ): CurrencyAmountModel => {
    if (!isUnifiedComplaintFlow || !complaint?.estimatedRefund) return fullOrderReturnPrice

    if (pricing === ReturnShippingOptionPricing.RefundSubtotal)
      return complaint.estimatedRefund.totalEstimatedRefund

    return complaint.estimatedRefund.totalEstimatedRefund
  }

  const getCustomShippingPriceWithNewEstimatedRefund = (
    pricing: ReturnShippingOptionPricing,
    fullOrderReturnPrice: CurrencyAmountModel,
  ): CurrencyAmountModel => {
    if (!isUnifiedComplaintFlow || !initialRefundEstimate) return fullOrderReturnPrice

    if (pricing === ReturnShippingOptionPricing.RefundSubtotal)
      return initialRefundEstimate.totalEstimatedRefund

    return initialRefundEstimate.totalEstimatedRefund
  }

  const getCustomShippingFormattedPrice = (pricing: PricingSummaryModel) => {
    if (isNewRefundSummaryEnabled) {
      return formatCurrencyAmount(
        getCustomShippingPriceWithNewEstimatedRefund(
          pricing.pricing,
          pricing.price as CurrencyAmountModel,
        ),
        locale,
      )
    }

    return formatCurrencyAmount(
      getCustomShippingPrice(pricing.pricing, pricing.price as CurrencyAmountModel),
      locale,
    )
  }

  const renderCustomReturnShippingPriceOption = () => {
    return customReturnShippingOption?.pricingSummary?.map(pricing => {
      if (!pricing.price) return null

      const isLastElement = pricing.pricing === ReturnShippingOptionPricing.RefundTotal
      const isRefundSubtotal = pricing.pricing === ReturnShippingOptionPricing.RefundSubtotal

      return (
        <Fragment key={pricing.pricing}>
          {isLastElement && <Spacer size={Spacer.Size.Large} />}
          <Cell
            testId="return-shipping-option-custom-price"
            styling={Cell.Styling.Tight}
            title={
              <div className="u-flexbox">
                {pricing.price && (
                  <Text
                    as={isLastElement ? 'h2' : 'span'}
                    type={isLastElement ? Text.Type.Title : undefined}
                    text={pricing.title}
                  />
                )}
                {isRefundSubtotal && renderRefundInfoIcon()}
              </div>
            }
            suffix={
              pricing.price && (
                <Text
                  as={isLastElement ? 'h2' : 'span'}
                  type={isLastElement ? Text.Type.Title : undefined}
                  text={getCustomShippingFormattedPrice(pricing)}
                />
              )
            }
          />
        </Fragment>
      )
    })
  }

  const renderCurrencyConvertionNote = () => {
    if (!returnShippingOptionsData?.currencyConversion) return null

    return (
      <>
        <span>
          <button type="button" onClick={showCurrencyConversion}>
            <Text
              clickable
              theme="inverse"
              type={Text.Type.Caption}
              text={currencyConversionTranslation('currency_conversion_details.learn_more_link')}
              as="span"
            />
          </button>
        </span>
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Text
          type={Text.Type.Caption}
          text={currencyConversionTranslation('currency_conversion_details.learn_more_about')}
          as="span"
        />
      </>
    )
  }

  const renderShippingPriceOption = () => {
    if (isNewRefundSummaryEnabled && !initialRefundEstimate)
      return <ContentLoader styling={ContentLoader.Styling.Wide} />

    return (
      <>
        <Cell>
          {isIntegratedReturnShippingOptionSelected
            ? renderIntegratedShippingPriceOption()
            : renderCustomReturnShippingPriceOption()}
          {renderCurrencyConvertionNote()}
        </Cell>
        <Divider />
      </>
    )
  }

  const trackSubmitClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
      selected_return_option: selectedShippingOption,
    }

    track(
      clickEvent({
        screen: 'return_order',
        target: 'confirm',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const handleSubmitClick = async (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (isReturnShippingOptionsLoading || isCreateReturnShipmentLoading) {
      event.preventDefault()

      return
    }

    trackSubmitClick()

    if (isIntegratedReturnShippingOptionSelected) {
      setActiveModal(null)
      onContinue()

      return
    }

    if (!transactionId || !selectedShippingOption) return

    const { error } = await fetchCreateReturnShipment({
      transactionId,
      returnShippingOption: selectedShippingOption,
    })

    if (error) {
      setActiveModal(ModalKeys.GenericErrorModal)

      return
    }

    handleModalClose()
  }

  const renderSubmit = () => {
    const isBuyerPayingForShipping = !integratedReturnShippingOption?.note

    const buttonText = isIntegratedReturnShippingOptionSelected
      ? integratedReturnShippingOption?.actionTitle
      : customReturnShippingOption?.actionTitle

    const buttonTestId = isIntegratedReturnShippingOptionSelected
      ? 'return-shipping-option-integrated-submit'
      : 'return-shipping-option-custom-submit'

    return (
      <>
        {isBuyerPayingForShipping && <Note text={translate('submit_note')} />}
        <Cell>
          <Button
            styling={Button.Styling.Filled}
            text={buttonText}
            isLoading={isReturnShippingOptionsLoading || isCreateReturnShipmentLoading}
            onClick={handleSubmitClick}
            testId={buttonTestId}
          />
        </Cell>
      </>
    )
  }

  const handleClose = () => {
    setActiveModal(ModalKeys.ReturnShippingOptionsModal)
  }

  const renderReturnShippingOptionsModal = () => {
    return (
      <Dialog show hasScrollableContent>
        <div className="u-fill-width">
          {renderNavigation()}
          <div className="modal-contained-content return-shipping-options-modal">
            {renderShowAllItemsReturnInfoBanner()}
            {renderShippingItems()}
            {renderItemsNote()}
            {renderItemVerificationInfoBanner()}
            {renderElectronicsVerificationInfoBanner()}
            {renderTitle()}
            {renderReturnShippingOptions()}
            {renderShippingPriceOption()}
            {renderSubmit()}
          </div>
        </div>
      </Dialog>
    )
  }

  const renderShippingDiscountModal = () => {
    const postagePrice = integratedReturnShippingOption?.pricingSummary?.find(
      pricingSummary => pricingSummary.pricing === ReturnShippingOptionPricing.Postage,
    )

    return (
      <ShippingDiscountModal
        isOpen
        onClose={handleClose}
        discount={postagePrice?.discount}
        lowestPriceIn30Days={postagePrice?.lowestPriceIn30days}
      />
    )
  }

  const renderComplaintCurrencyConversionModal = () => {
    const isSellerBusinessUser = isComplaintOrderSellerBusinessUser(complaint?.orderType)

    return isNewRefundSummaryEnabled ? (
      <BuyerCurrencyConversionModal
        isOpen
        refundEstimate={refundEstimate}
        isOpenedInDialog
        onClose={handleClose}
      />
    ) : (
      <ComplaintCurrencyConversionModal
        isOpen
        selectedShippingOption={selectedShippingOption}
        integratedReturnShippingOption={integratedReturnShippingOption}
        refundEstimate={complaint?.estimatedRefund}
        isSellerBusinessUser={isSellerBusinessUser}
        isOpenedInDialog
        onClose={handleClose}
      />
    )
  }

  const renderReturnCurrencyConversionModal = () => {
    if (complaint?.estimatedRefund || refundEstimate)
      return renderComplaintCurrencyConversionModal()

    return (
      <ReturnCurrencyConversionModal
        isOpen
        conversion={returnShippingOptionsData?.currencyConversion}
        selectedShippingOption={selectedShippingOption}
        onClose={handleClose}
      />
    )
  }

  const renderBuyerRefundModal = () => {
    const isSellerBusinessUser = isComplaintOrderSellerBusinessUser(complaint?.orderType)

    if (isNewRefundSummaryEnabled)
      return (
        <BuyerRefundInformation
          note={integratedReturnShippingOption?.note ? undefined : translate('submit_note')}
          selectedShippingOption={selectedShippingOption}
          isBundleComplaint={complaint?.isBundleOrder}
          transactionId={transactionId}
          refundEstimate={refundEstimate}
          isOpen
          onClose={handleClose}
          isSellerBusinessUser={isSellerBusinessUser}
          isOpenedInDialog
          isLoading={isRefundEstimateLoading}
        />
      )

    return (
      <EstimatedRefundInformation
        note={integratedReturnShippingOption?.note ? undefined : translate('submit_note')}
        selectedShippingOption={selectedShippingOption}
        integratedReturnShippingOption={integratedReturnShippingOption}
        isBundleComplaint={complaint?.isBundleOrder}
        transactionId={transactionId}
        refundEstimate={complaint?.estimatedRefund}
        isOpen
        onClose={handleClose}
        isSellerBusinessUser={isSellerBusinessUser}
        isOpenedInDialog
      />
    )
  }

  const renderActiveModal = () => {
    switch (activeModal) {
      case ModalKeys.ReturnShippingOptionsModal:
        return renderReturnShippingOptionsModal()
      case ModalKeys.ShippingDiscountModal:
        return renderShippingDiscountModal()
      case ModalKeys.ReturnCurrencyConversionModal:
        return renderReturnCurrencyConversionModal()
      case ModalKeys.EstimatedRefundInformation:
        return renderBuyerRefundModal()
      case ModalKeys.GenericErrorModal:
        return <GenericErrorModal isShown onClose={handleModalClose} />
      default:
        return null
    }
  }

  return renderActiveModal()
}

export default ReturnShippingOptionsModal
